import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../shared/AnimatedSection';
const GOLD = '#C9A84C';
/**
 * Premium emotional pain section — sits between Hero and BigIdea.
 * Communicates "the problem was never the outside world" before the
 * user is presented with the 12 Reinos selector.
 *
 * Visual language matches existing dark cinematic premium aesthetic:
 * - bg-cosmic-black with soft radial gold glow
 * - subtle floating gold particles
 * - faint sacred geometry SVG
 * - serif editorial typography (no new fonts)
 */
export function EmotionalPain() {
  return <section className="relative py-24 md:py-32 px-6 overflow-hidden bg-cosmic-black" data-id="element-156">
      {/* Radial atmospheric backdrop */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-157">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 30%, rgba(201,168,76,0.07) 0%, transparent 60%)'
      }} data-id="element-158" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #050508 0%, rgba(10,10,18,1) 50%, #050508 100%)'
      }} data-id="element-159" />
      </div>

      {/* Sacred geometry — subtle, centered, behind text */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.06]" data-id="element-160">
        <motion.svg width="640" height="640" viewBox="0 0 640 640" fill="none" animate={{
        rotate: 360
      }} transition={{
        duration: 240,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-161">
          <circle cx="320" cy="320" r="280" stroke={GOLD} strokeWidth="0.6" data-id="element-162" />
          <circle cx="320" cy="320" r="200" stroke={GOLD} strokeWidth="0.6" data-id="element-163" />
          <circle cx="320" cy="320" r="120" stroke={GOLD} strokeWidth="0.6" data-id="element-164" />
          {[0, 30, 60, 90, 120, 150].map(a => <line key={a} x1="320" y1="40" x2="320" y2="600" stroke={GOLD} strokeWidth="0.4" transform={`rotate(${a} 320 320)`} data-id="element-165" />)}
        </motion.svg>
      </div>

      {/* Floating gold particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-166">
        {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: GOLD,
        left: `${i * 8.5 % 100}%`,
        top: `${i * 13 % 100}%`,
        boxShadow: `0 0 6px ${GOLD}`
      }} animate={{
        y: [-12, -40, -12],
        opacity: [0.15, 0.55, 0.15]
      }} transition={{
        duration: 7 + i % 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.5
      }} data-id="element-167" />)}
      </div>

      <div className="relative z-10 container mx-auto max-w-3xl text-center" data-id="element-168">
        <AnimatedSection data-id="element-169">
          <motion.div className="inline-flex items-center gap-3 mb-8" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-170">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold-primary" data-id="element-171" />
            <span className="tracking-[0.4em] uppercase font-serif text-gold-light text-[14px]" data-id="element-172">
              El Patrón Que Se Repite
            </span>
            <div className="h-px w-12 bg-gradient-to-r from-gold-primary to-transparent" data-id="element-173" />
          </motion.div>

          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-10 leading-tight px-2" style={{
          textShadow: `0 0 30px rgba(201,168,76,0.25)`
        }} data-id="element-174">
            Tal vez no estás perdido.{' '}
            <span className="text-gold-light" data-id="element-175">
              Tal vez estás repitiendo un patrón.
            </span>
          </h2>
        </AnimatedSection>

        <AnimatedSection delay={0.2} data-id="element-176">
          <div className="space-y-6 text-base md:text-lg leading-relaxed text-gray-200 font-serif px-2" data-id="element-177">
            <p className="font-[Georgia,_serif]" data-id="element-178">
              Relaciones que se repiten. Emociones que no entiendes. Versiones
              de ti que aparecen una y otra vez.
            </p>
            <p className="text-gold-light/90 italic text-[16px] font-['DM_Sans',_sans-serif]" data-id="element-179">
              LOS 12 fue creado para ayudarte a descubrir el Reino interno que
              está moldeando tu forma de pensar, sentir, amar y reaccionar.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}